import { Menu } from '@mantine/core'
import { IconAppWindow, IconBrowser, IconExternalLink, IconLayout, IconUserPlus } from '@tabler/icons-react'
import { useNavigate } from '@tanstack/react-router'
import { useId, type ReactElement } from 'react'
import { useSinglePortalContextMenu } from '../contextMenuState'
import { usePortalTabs } from '../usePortalTabs'

type StudentSelectContextMenuProps = {
    studentNo: string
    children: ReactElement
}

function getStudentUrl(studentNo: string) {
    return `/students/${encodeURIComponent(studentNo)}`
}

export function StudentSelectContextMenu({ studentNo, children }: StudentSelectContextMenuProps) {
    const navigate = useNavigate()
    const { addStudent, openStudent, openStudentModal } = usePortalTabs()
    const contextMenuId = useId()
    const contextMenu = useSinglePortalContextMenu(`student-select:${studentNo}:${contextMenuId}`)
    const studentUrl = getStudentUrl(studentNo)

    const openInCurrentRoute = () => {
        void navigate({
            to: '/students/$studentNo',
            params: { studentNo },
        } as never)
    }

    return (
        <Menu
            shadow="md"
            width={220}
            position="bottom-start"
            offset={0}
            opened={contextMenu.opened}
            onChange={contextMenu.onChange}
        >
            <Menu.ContextMenu>
                {children}
            </Menu.ContextMenu>
            <Menu.Dropdown>
                <Menu.Label>Student profile</Menu.Label>
                <Menu.Item leftSection={<IconAppWindow size={16} />} onClick={() => openStudentModal(studentNo)}>
                    Open in modal
                </Menu.Item>
                <Menu.Item leftSection={<IconLayout size={16} />} onClick={() => openStudent(studentNo)}>
                    Open as tab
                </Menu.Item>
                <Menu.Item leftSection={<IconUserPlus size={16} />} onClick={() => addStudent(studentNo)}>
                    Add tab
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item leftSection={<IconExternalLink size={16} />} onClick={openInCurrentRoute}>
                    Open in current page
                </Menu.Item>
                <Menu.Item
                    leftSection={<IconBrowser size={16} />}
                    onClick={() => window.open(studentUrl, '_blank', 'noopener,noreferrer')}
                >
                    Open in browser tab
                </Menu.Item>
                <Menu.Item
                    leftSection={<IconAppWindow size={16} />}
                    onClick={() => window.open(
                        studentUrl,
                        `student-record-${studentNo}`,
                        'popup=yes,width=1200,height=850,noopener,noreferrer',
                    )}
                >
                    Open in new window
                </Menu.Item>
            </Menu.Dropdown>
        </Menu>
    )
}
